/* eslint-disable no-unused-expressions */
import React, { useEffect, useMemo, useState } from 'react';
import * as PropTypes from 'prop-types';
import LogoInput from '../BenefitOfferAdmin/LogoInput';
import NewsfeedButton from '../Newsfeed/Button';

const ArticleAdminModal = ({
    type = 'edit',
    postArticle = () => {},
    posting,
    preFetchedArticle = {},
    isAllSiteSelected = false,
    sites = [],
    languages = [],
    sections = [],
    subsections = [],
}) => {
    const article = (() => { try { return JSON.parse(preFetchedArticle || {}); } catch { return {}; } })();
    const languagesList = (() => { try { return JSON.parse(languages || []); } catch { return []; } })();
    const sectionsList = (() => { try { return JSON.parse(sections || []); } catch { return []; } })();
    const subsectionsList = (() => { try { return JSON.parse(subsections || []); } catch { return []; } })();
    const unsortedSites = (() => { try { return JSON.parse(sites || []); } catch { return []; } })();

    const [logo, setLogo] = useState(null);
    const [title, setTitle] = useState('');
    const [languageCode, setLanguageCode] = useState('');
    const [section, setSection] = useState('');
    const [subsectionId, setSubsectionId] = useState('');
    const [authorName, setAuthorName] = useState('');
    const [publisherName, setPublisherName] = useState('');
    const [publisherUrl, setPublisherUrl] = useState('');
    const [readingTime, setReadingTime] = useState('');
    const [active, setActive] = useState(false);
    const [selectedSites, setSelectedSites] = useState([]);

    const allSites = useMemo(() => {
        return [...unsortedSites].sort((a, b) => {
            const nameA = (a?.topLocation?.name || '').toUpperCase();
            const nameB = (b?.topLocation?.name || '').toUpperCase();
            return nameA < nameB ? -1 : nameA > nameB ? 1 : 0;
        });
    }, [unsortedSites]);

    const filteredSubsections = useMemo(() => {
        if (!Array.isArray(subsectionsList)) return [];
        return subsectionsList.filter((subsection) => {
            const code = subsection?.learnSection?.learnSectionCode;
            return !section || code === section;
        });
    }, [subsectionsList, section]);

    const handleFormSubmit = (e) => {
        e.preventDefault();
        postArticle(
            logo,
            title,
            languageCode,
            section,
            subsectionId,
            authorName,
            publisherName,
            publisherUrl,
            readingTime,
            active,
            selectedSites
        );
    };

    const hideModal = () => {
        const modalWrapper = document.querySelector('.modal-wrapper');
        if (modalWrapper) modalWrapper.classList.toggle('hidden');
    };

    useEffect(() => {
        setLogo(article?.logo?.src ?? null);
        setTitle(article?.title ?? '');
        setLanguageCode(article?.languageCode ?? '');
        setSection(article?.sectionCode ?? '');
        setSubsectionId(article?.learnSubsectionId ?? '');
        setAuthorName(article?.authorName ?? '');
        setPublisherName(article?.publisherName ?? '');
        setPublisherUrl(article?.publisherUrl ?? '');
        setReadingTime(article?.readingTime ?? '');
        setActive(!!article?.active);

        if (isAllSiteSelected) {
            setSelectedSites(allSites.map((site) => site.siteCode));
        } else {
            const codes = Array.isArray(article?.sitesCodes)
                ? article.sitesCodes.map((site) => site.code ?? site)
                : [];
            setSelectedSites(codes);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preFetchedArticle, isAllSiteSelected, allSites.length]);

    const setArticleSite = (value) => {
        let sel = selectedSites;
        if (value === '') {
            if (selectedSites.length === allSites.length) {
                sel = [];
            } else {
                sel = allSites.map((site) => site.siteCode);
            }
        } else if (selectedSites.includes(value)) {
            sel = selectedSites.filter((site) => site !== value);
        } else {
            sel = selectedSites.concat(value);
        }
        setSelectedSites(sel);
    };

    return (
        <div
            tabIndex="-1"
            className="overflow-y-auto overflow-x-hidden fixed top-0 right-0 left-0 z-50 w-full md:inset-0 h-full md:h-screen justify-center items-center flex bg-black/90 md:pb-12 pb-14"
            role="dialog"
        >
            <div className="bg-white rounded-2xl py-6 md:w-1/2 xl:w-2/5 w-11/12 max-w-full h-auto m-auto md:mt-12 mt-14 flex flex-col gap-4">
                <div className="flex items-center justify-between px-6">
                    <h2 className="text-gray-800 font-semibold md:text-xl text-lg leading-8 mb-0">
                        {type === 'add' ? 'Add' : 'Edit'} an article
                    </h2>
                    <button type="button" onClick={hideModal} className="inline-flex items-center justify-center w-6 h-6">
                        <span className="material-icons text-gray-600">close</span>
                    </button>
                </div>

                <div className="w-full py-2 md:px-6 px-4 flex items-start gap-6">
                    <form onSubmit={handleFormSubmit} className="flex w-full flex-col gap-3">
                        <div className="w-full">
                            <p className="font-semibold text-gray-800 text-lg mb-2">Logo</p>
                            <LogoInput
                                handleLogoChange={(file) => setLogo(file)}
                                removeImage={() => setLogo(null)}
                                prefetchedLogo={logo}
                            />
                        </div>

                        <label htmlFor="title" className="font-semibold text-gray-800 text-lg">
                            Title
                            <input
                                id="title"
                                type="text"
                                className="text-left p-3 border w-full border-gray-300 placeholder-gray-200 mt-2 rounded-sm focus:outline-hidden focus:border-blue-500"
                                onChange={(e) => setTitle(e.target.value)}
                                value={title || ''}
                                required
                            />
                        </label>

                        <label htmlFor="language" className="font-semibold text-gray-800 text-lg">
                            Language
                            <select
                                id="language"
                                className="mt-2 mb-2 w-full bg-white border border-gray-300 focus:border-blue-600 focus:outline-hidden text-gray-800 text-base leading-6 rounded p-2.5 appearance-none cursor-pointer"
                                onChange={(e) => setLanguageCode(e.target.value)}
                                value={languageCode || ''}
                                required
                            >
                                <option value="">— Select language —</option>
                                {languagesList.map((language) => (
                                    <option key={language?.languageCode} value={language?.languageCode}>
                                        {language?.name} ({language?.languageCode})
                                    </option>
                                ))}
                            </select>
                        </label>

                        <label htmlFor="section" className="font-semibold text-gray-800 text-lg">
                            Category
                            <select
                                id="section"
                                className="mt-2 mb-2 w-full bg-white border border-gray-300 focus:border-blue-600 focus:outline-hidden text-gray-800 text-base leading-6 rounded p-2.5 appearance-none cursor-pointer"
                                onChange={(e) => {
                                    setSubsectionId('');
                                    setSection(e.target.value);
                                }}
                                value={section || ''}
                            >
                                <option value="">— Select category —</option>
                                {sectionsList?.map((section) => (
                                    <option key={section?.learnSectionCode} value={section?.learnSectionCode}>
                                        {section?.name}
                                    </option>
                                ))}
                            </select>
                        </label>

                        <label htmlFor="subsection" className="font-semibold text-gray-800 text-lg">
                            Subcategory
                            <select
                                id="subsection"
                                className="mt-2 mb-2 w-full bg-white border border-gray-300 focus:border-blue-600 focus:outline-hidden text-gray-800 text-base leading-6 rounded p-2.5 appearance-none cursor-pointer"
                                onChange={(e) => setSubsectionId(e.target.value)}
                                value={subsectionId || ''}
                            >
                                <option value="">— Select subcategory —</option>
                                {filteredSubsections.map((subsection) => (
                                    <option key={subsection?.learnSubsectionId} value={subsection?.learnSubsectionId}>
                                        {subsection?.name}
                                    </option>
                                ))}
                            </select>
                        </label>

                        <label htmlFor="authorName" className="font-semibold text-gray-800 text-lg">
                            Author name
                            <input
                                id="authorName"
                                type="text"
                                className="text-left p-3 border w-full border-gray-300 placeholder-gray-200 mt-2 rounded-sm focus:outline-hidden focus:border-blue-500"
                                onChange={(e) => setAuthorName(e.target.value)}
                                value={authorName || ''}
                                required
                            />
                        </label>

                        <label htmlFor="publisherName" className="font-semibold text-gray-800 text-lg">
                            Publisher name
                            <input
                                id="publisherName"
                                type="text"
                                className="text-left p-3 border w-full border-gray-300 placeholder-gray-200 mt-2 rounded-sm focus:outline-hidden focus:border-blue-500"
                                onChange={(e) => setPublisherName(e.target.value)}
                                value={publisherName || ''}
                                required
                            />
                        </label>

                        <label htmlFor="publisherUrl" className="font-semibold text-gray-800 text-lg">
                            Publisher URL
                            <input
                                id="publisherUrl"
                                type="url"
                                className="text-left p-3 border w-full border-gray-300 placeholder-gray-200 mt-2 rounded-sm focus:outline-hidden focus:border-blue-500"
                                onChange={(e) => setPublisherUrl(e.target.value)}
                                value={publisherUrl || ''}
                                required
                            />
                        </label>

                        <label htmlFor="readingTime" className="font-semibold text-gray-800 text-lg">
                            Reading time (mins)
                            <input
                                id="readingTime"
                                type="number"
                                className="text-left p-3 border w-full border-gray-300 placeholder-gray-200 mt-2 rounded-sm focus:outline-hidden focus:border-blue-500"
                                onChange={(e) => setReadingTime(e.target.value)}
                                value={readingTime || ''}
                                required
                            />
                        </label>

                        <div className="flex items-center gap-2 mt-2">
                            <input
                                id="active"
                                type="checkbox"
                                checked={!!active}
                                onChange={(e) => setActive(e.target.checked)}
                            />
                            <label htmlFor="active" className="font-semibold text-gray-800 text-lg">
                                Active
                            </label>
                        </div>

                        <label htmlFor="site" className="font-semibold text-gray-800 text-lg">
                            Enabled on sites
                            <select
                                size="12"
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    e.currentTarget.focus();
                                    const el = e.target;
                                    const { scrollTop } = el.parentNode;
                                    setTimeout(() => el.parentNode.scrollTo(0, scrollTop), 1);
                                    setArticleSite(e.target.value);
                                }}
                                onChange={(e) => e.preventDefault()}
                                multiple
                                value={selectedSites}
                                id="site"
                                className="mt-2 mb-2 w-full bg-white border border-gray-300 focus:border-blue-600 focus:outline-hidden text-gray-800 text-base leading-6 rounded p-2.5 appearance-none cursor-pointer"
                            >
                                <option value="">All Sites</option>
                                {allSites?.map((site) => (
                                    <option key={site?.siteCode} value={site?.siteCode}>
                                        {site?.topLocation?.name} - ({site?.siteCode})
                                    </option>
                                ))}
                            </select>
                        </label>

                        <div className="w-full flex items-center justify-end">
                            <NewsfeedButton classNames="md:w-auto w-full" loading={posting} title="Submit" type="primary" />
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ArticleAdminModal;

ArticleAdminModal.propTypes = {
    type: PropTypes.oneOf(['add', 'edit']),
    postArticle: PropTypes.func,
    posting: PropTypes.bool.isRequired,
    preFetchedArticle: PropTypes.string,
    isAllSiteSelected: PropTypes.bool,
    sites: PropTypes.string,
    languages: PropTypes.string,
    sections: PropTypes.string,
    subsections: PropTypes.string,
};
